import { Base } from '@studiometa/js-toolkit';
import { untrapFocus, trapFocus } from '@studiometa/js-toolkit/utils';

/**
 * Facets component
 */
export default class Search extends Base {
  timeout = null;

  /**
   * Component Configuration
   */
  static config = {
    name: 'Search',
    refs: ['form', 'input', 'close', 'container', 'loader'],
  };

  /**
   * Set Default container
   * @returns {void}
   */
  mounted() {
    this.defaultContainer = this.$refs.container.innerHTML;
  }

  /**
   * Get input value
   * @returns {string} current value.
   */
  get searchTerm() {
    return this.$refs.input.value;
  }

  /**
   * Perform search when typing something in input
   * @returns {void}
   */
  onInputInput() {
    this.$refs.loader.style.display = 'block';

    clearTimeout(this.timeout);

    if (!this.searchTerm) {
      this.$refs.loader.style.display = 'none';
      this.$refs.container.innerHTML = this.defaultContainer;
      return;
    }

    this.timeout = setTimeout(() => {
      Promise.all([
        fetch(
          `${window.routes.predictive_search_url}?q=${encodeURIComponent(this.searchTerm)}&resources[type]=product&resources[options][fields]=title,body,variants.title&section_id=predictive-search`,
        ),
        fetch(`/search?q=${encodeURIComponent(this.searchTerm)}`),
      ])
        .then(([predictiveResponse, searchResponse]) => {
          if (!predictiveResponse.ok || !searchResponse.ok) {
            const error = new Error(predictiveResponse.status);
            throw error;
          }

          return Promise.all([predictiveResponse.text(), searchResponse.text()]);
        })
        .then(([predictiveText, searchText]) => {
          const predictive_dom = new DOMParser().parseFromString(predictiveText, 'text/html');
          const predictive_section = predictive_dom.querySelector(
            '#shopify-section-predictive-search',
          );

          const searchCount = new DOMParser()
            .parseFromString(searchText, 'text/html')
            .querySelector('#search-count');

          if (predictive_section.querySelector('#predictive-count')) {
            predictive_section.querySelector('#predictive-count').innerHTML =
              searchCount.dataset.count;
          }

          const results = predictive_section.innerHTML;

          this.renderResults(results);
          this.initAllResultsBtn();
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          throw error;
        });
    }, 400);
  }

  /**
   *
   */
  initAllResultsBtn() {
    this.$el.querySelector('#all-results').addEventListener('click', () => {
      this.$refs.form.requestSubmit();
    });
  }

  /**
   * Set results to the page
   * @param  {HTML} results products results
   * @returns {void}
   */
  renderResults(results) {
    this.$refs.loader.style.display = 'none';
    this.$refs.container.innerHTML = results;
  }

  /**
   * Open Search
   * @returns {void}
   */
  open() {
    document.addEventListener('keyup', (event) => {
      trapFocus(this.$el, event);
    });
    document.body.classList.add('search-open');
    this.$el.classList.remove('pointer-events-none');
    this.$el.setAttribute('aria-hidden', 'false');
    this.$el.removeAttribute('inert');
    this.$refs.input.focus();
  }

  /**
   * Close search
   * @returns {void}
   */
  close() {
    untrapFocus();
    document.body.classList.remove('search-open');
    this.$el.classList.add('pointer-events-none');

    this.$el.setAttribute('aria-hidden', 'true');
    this.$el.setAttribute('inert', '');
    this.$refs.input.value = '';
  }

  /**
   * Click on close btn
   * @returns {void}
   */
  onCloseClick() {
    this.close();
  }

  /**
   * Close panel when type on Escape key
   * @param {object} props Props
   * @param {object} props.ESC Escape key
   * @returns {void}
   */
  keyed({ ESC }) {
    if (ESC) {
      this.close();
    }
  }
}
